import {useRef, useState, useEffect,} from 'react';
import { TSuggestions, TUseSuggestionsParams } from '../../../../../../../types/dealerLocator.types';

const DEBOUNCE_DELAY = 500;
const MIN_SEARCH_TERM_LENGTH = 3;

const useSuggestions = (query: string, inputText: string, autocompleteService: google.maps.places.AutocompleteService, {countryIsoCode = '', resultsLimit}: TUseSuggestionsParams): [TSuggestions] => {
    const [suggestions, setSuggestions] = useState<TSuggestions | null>(null);
    const controllerRef = useRef<AbortController | null>();
    const timerRef = useRef<ReturnType<typeof setTimeout>>();
  
    const debounceFn = (callback) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(callback, DEBOUNCE_DELAY);
    } 

    const getComponentsParam = (): {country: Array<string>}  | undefined => {
      if(!countryIsoCode){
        return;
      }
      
      return {
        country:Array.isArray(countryIsoCode) ? countryIsoCode : [countryIsoCode]
      };
    }

    const fetchPlacesFromGoogle = () => {
      const req = {
        input: query,
        types: ['geocode'],
        componentRestrictions: getComponentsParam(),
      } 

      return new Promise<google.maps.places.AutocompletePrediction[] | null>((resolve, reject) => {
        autocompleteService.getPlacePredictions(req, (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK){
            resolve(results);
          }else{
            reject('Failed to fetch place detail from Google Places API'); // eslint-disable-line
          }
        })
      });
    }
  
    const fetchSuggestions = (): void => {  
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      const controller = new AbortController(); 
      controllerRef.current = controller;

      if(query?.length < MIN_SEARCH_TERM_LENGTH){
        setSuggestions(null);
        return;
      }  

      Promise.allSettled([fetchPlacesFromGoogle()])
        .then((promiseResults) => {
          const [googlePlacesResuls] = promiseResults;
          const data: TSuggestions = {googlePlacesPredictions: []};
          let predictionsResults: google.maps.places.AutocompletePrediction[] = [];

          if(googlePlacesResuls?.status === 'fulfilled'){
            predictionsResults = googlePlacesResuls?.value || [];
          }


          data['googlePlacesPredictions'] = predictionsResults?.slice(0, resultsLimit) || [];

          setSuggestions(data)
        });
    }
  
    useEffect(() => {
      try{
        if(inputText !== query){
            setSuggestions(null);
            return;
        }

        debounceFn(() => fetchSuggestions());
      }catch(e) {
        setSuggestions(null);
      }
  
    }, [query, inputText])
  
    return [suggestions];
}

export default useSuggestions;